<template>
  <div
    :class="['rpilot-sidebar', `rpilot-sidebar--${mode}`, attrs.class]"
    :style="attrs.style"
  >
    <!-- Minimised rail. The chat below stays mounted (v-show) so an in-flight
         turn, the session and any paused frontend tool call survive. -->
    <button
      v-if="minimised"
      type="button"
      class="rpilot-sidebar-rail"
      title="Show RPilot"
      aria-label="Show RPilot"
      @click="setMinimised(false)"
    >
      <v-badge :model-value="unread" color="primary" dot>
        <img :src="rpilotIcon" alt="" width="28" height="28" />
      </v-badge>
      <v-progress-circular
        v-if="chatStore.isBusy"
        indeterminate
        size="18"
        width="2"
        color="primary"
      />
      <v-icon icon="mdi-chevron-double-left" size="small" />
    </button>

    <!-- v-show lives on a wrapper: the widget's root carries Vuetify's
         d-flex (display: flex !important), which beats v-show's display:none. -->
    <div v-show="!minimised" class="rpilot-sidebar-chat">
      <RPilotChat
        ref="chatRef"
        v-bind="chatAttrs"
        expandable
        :expanded="expanded"
        @update:expanded="setExpanded"
      >
        <template #header-actions>
          <v-btn
            icon="mdi-chevron-double-right"
            size="small"
            variant="text"
            title="Minimise"
            aria-label="Minimise"
            @click="setMinimised(true)"
          />
        </template>
      </RPilotChat>
    </div>
  </div>
</template>

<script setup>
// Chat sidebar shared by the RPilot pages: docked (fixed width), expanded
// (most of the page, for wide agent output such as markdown tables) or
// minimised to a rail. Everything other than class/style is forwarded to
// <RPilotChat>, so props and @tool-call etc. are passed as on the widget.
import { ref, computed, watch, nextTick, useAttrs } from 'vue';
import { useChatStore } from '@rpilot/chat/stores/chat.js';
import rpilotIcon from '@/assets/rpilot-icon.svg';

defineOptions({ inheritAttrs: false });

const STORAGE_KEY = 'rpilot-sidebar-layout';

const attrs = useAttrs();
const chatAttrs = computed(() => {
  // eslint-disable-next-line no-unused-vars
  const { class: _class, style: _style, ...rest } = attrs;
  return rest;
});

const chatStore = useChatStore();
const chatRef = ref(null);

const readLayout = () => {
  try {
    return JSON.parse(localStorage.getItem(STORAGE_KEY)) || {};
  } catch {
    return {};
  }
};
const saved = readLayout();
const expanded = ref(saved.expanded === true);
const minimised = ref(saved.minimised === true);
const unread = ref(false);

const mode = computed(() => {
  if (minimised.value) return 'minimised';
  return expanded.value ? 'expanded' : 'docked';
});

watch([expanded, minimised], () => {
  try {
    localStorage.setItem(
      STORAGE_KEY,
      JSON.stringify({ expanded: expanded.value, minimised: minimised.value })
    );
  } catch {
    // storage blocked — layout just won't be remembered
  }
});

// Flag new agent output that arrives while the chat is hidden.
watch(
  () => chatStore.messages.length,
  (next, prev) => {
    if (minimised.value && next > prev) unread.value = true;
  }
);

const setExpanded = (next) => {
  expanded.value = next;
};

const setMinimised = async (next) => {
  minimised.value = next;
  if (next) return;
  unread.value = false;
  // The message list lost its scroll position while display:none, and its
  // auto-scroll can't move a hidden element — jump to the latest message.
  await nextTick();
  const list = chatRef.value?.$el?.querySelector?.('.message-list');
  if (list) list.scrollTop = list.scrollHeight;
};

defineExpose({
  sendMessage: (text) => chatRef.value?.sendMessage(text),
  newChat: () => chatRef.value?.newChat(),
  stop: () => chatRef.value?.stop(),
});
</script>

<style scoped>
.rpilot-sidebar {
  border: 1px solid rgba(var(--v-border-color), var(--v-border-opacity, 0.12));
  border-radius: 8px;
  display: flex;
  overflow: hidden;
  background: rgb(var(--v-theme-surface));
}
.rpilot-sidebar--docked {
  width: 420px;
  flex: 0 0 420px;
}
/* Leaves the preview a usable strip so proposals stay visible beside the
   chat; max() keeps the docked width as the floor on narrow viewports. */
.rpilot-sidebar--expanded {
  width: max(420px, 65%);
  flex: 0 0 max(420px, 65%);
}
.rpilot-sidebar--minimised {
  width: 48px;
  flex: 0 0 48px;
}
.rpilot-sidebar-chat {
  flex: 1 1 auto;
  min-height: 0;
  min-width: 0;
  display: flex;
}
.rpilot-sidebar-chat > * {
  flex: 1 1 auto;
  min-height: 0;
  min-width: 0;
}
.rpilot-sidebar-rail {
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
  padding: 12px 0;
  background: none;
  border: 0;
  color: inherit;
  cursor: pointer;
}
.rpilot-sidebar-rail:hover {
  background: rgba(var(--v-theme-on-surface), 0.04);
}
/* App.vue globally sets .v-card { margin: 0 auto }, which centres the chat
   bubbles (they're v-cards with a max-width) and defeats the widget's own
   flex alignment (user right, agent left). Reset it inside the chat only. */
.rpilot-sidebar :deep(.v-card) {
  margin: 0;
}
</style>
